import React, { useCallback, useEffect, useRef } from "react";
import { Box, Stack, Typography } from "@mui/material";
import {
  clamp01,
  gamutChroma,
  inGamut,
  MAX_CHROMA,
  OKLCh,
  oklabToRGB,
  oklchToCSS,
} from "../../oklab";

// A colour chosen in OKLCh — the space the stone colours are measured in, so the distance the
// search ranks by is the one the eye sees. Chroma runs left to right and lightness bottom to top
// over a hue held by the strip below, which is the same shape as any other colour picker but with
// each axis meaning what it says.
const AREA_WIDTH = 240;
const AREA_HEIGHT = 150;
const STRIP_HEIGHT = 24;

// The markers, and how far their centres stay from an edge so that neither is ever cut in half by
// it: a colour at chroma 0 or hue 0 sits at the very edge of its canvas.
const DOT_SIZE = 14;
const BAR_WIDTH = 6;

// The lightness and chroma the hue strip is drawn at: bright and colourful enough to tell the hues
// apart, held inside the gamut so no part of the strip flattens out.
const STRIP_LIGHTNESS = 0.75;
const STRIP_CHROMA = 0.2;

const MARKER_STYLE = {
  position: "absolute",
  pointerEvents: "none",
  border: "2px solid #fff",
  boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.55)",
} as const;

// Where a pointer landed, as a 0..1 position along each axis of the element it landed on.
function positionIn(event: React.PointerEvent<HTMLCanvasElement>) {
  const bounds = event.currentTarget.getBoundingClientRect();
  return {
    x: clamp01((event.clientX - bounds.left) / bounds.width),
    y: clamp01((event.clientY - bounds.top) / bounds.height),
  };
}

// A canvas ready to be painted, with its drawing buffer set to the size it is shown at. The buffer
// is the width and height *attributes*: left to their default the canvas would still be 300 by 150
// and the drawing would be scaled into the box, ending short of its edges.
function sized(canvas: HTMLCanvasElement | null, height: number) {
  if (!canvas) return null;
  canvas.width = AREA_WIDTH;
  canvas.height = height;
  return canvas.getContext("2d");
}

// A marker's centre, held far enough inside its canvas that the marker is drawn whole. Without it
// the ends of every axis — chroma 0, lightness 1, hue 0 — put half the marker outside the colour.
const inset = (position: number, span: number, marker: number) =>
  Math.min(Math.max(position, marker / 2), span - marker / 2);

const OKLChPicker = ({
  value,
  onChange,
}: {
  value: OKLCh;
  onChange: (color: OKLCh) => void;
}) => {
  const areaRef = useRef<HTMLCanvasElement | null>(null);
  const stripRef = useRef<HTMLCanvasElement | null>(null);

  // Past the gamut edge a row holds its last reachable colour, so the area stays a full rectangle
  // rather than showing the gamut's wedge. The cap depends only on the row, so it is found once per
  // row instead of once per pixel.
  useEffect(() => {
    const context = sized(areaRef.current, AREA_HEIGHT);
    if (!context) return;

    const radians = (value.h * Math.PI) / 180;
    const cosine = Math.cos(radians);
    const sine = Math.sin(radians);
    const image = context.createImageData(AREA_WIDTH, AREA_HEIGHT);
    for (let y = 0; y < AREA_HEIGHT; y += 1) {
      const lightness = 1 - y / (AREA_HEIGHT - 1);
      const cap = gamutChroma(lightness, value.h);
      for (let x = 0; x < AREA_WIDTH; x += 1) {
        const chroma = Math.min((x / (AREA_WIDTH - 1)) * MAX_CHROMA, cap);
        const rgb = oklabToRGB(lightness, chroma * cosine, chroma * sine);
        const offset = (y * AREA_WIDTH + x) * 4;
        image.data[offset] = clamp01(rgb[0]) * 255;
        image.data[offset + 1] = clamp01(rgb[1]) * 255;
        image.data[offset + 2] = clamp01(rgb[2]) * 255;
        image.data[offset + 3] = 255;
      }
    }
    context.putImageData(image, 0, 0);
  }, [value.h]);

  useEffect(() => {
    const context = sized(stripRef.current, STRIP_HEIGHT);
    if (!context) return;

    const image = context.createImageData(AREA_WIDTH, STRIP_HEIGHT);
    for (let x = 0; x < AREA_WIDTH; x += 1) {
      const hue = (x / (AREA_WIDTH - 1)) * 360;
      const radians = (hue * Math.PI) / 180;
      const chroma = Math.min(STRIP_CHROMA, gamutChroma(STRIP_LIGHTNESS, hue));
      const rgb = oklabToRGB(
        STRIP_LIGHTNESS,
        chroma * Math.cos(radians),
        chroma * Math.sin(radians),
      );
      for (let y = 0; y < STRIP_HEIGHT; y += 1) {
        const offset = (y * AREA_WIDTH + x) * 4;
        image.data[offset] = clamp01(rgb[0]) * 255;
        image.data[offset + 1] = clamp01(rgb[1]) * 255;
        image.data[offset + 2] = clamp01(rgb[2]) * 255;
        image.data[offset + 3] = 255;
      }
    }
    context.putImageData(image, 0, 0);
  }, []);

  // A press anywhere moves the colour there and starts a drag; capturing the pointer keeps the
  // drag alive when it wanders off the canvas.
  const drag = useCallback(
    (move: (event: React.PointerEvent<HTMLCanvasElement>) => void) =>
      (event: React.PointerEvent<HTMLCanvasElement>) => {
        if (event.buttons !== 1 && event.type !== "pointerdown") return;
        if (event.type === "pointerdown") {
          event.currentTarget.setPointerCapture(event.pointerId);
        }
        move(event);
      },
    [],
  );

  const pickInArea = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const { x, y } = positionIn(event);
    onChange({ ...value, c: x * MAX_CHROMA, l: 1 - y });
  };

  const pickHue = (event: React.PointerEvent<HTMLCanvasElement>) => {
    onChange({ ...value, h: positionIn(event).x * 360 });
  };

  // The marker goes wherever it is dragged inside the area, including the flat part past the gamut
  // edge where the colour has stopped changing. What is reported is the colour that part actually
  // shows — the swatch, the readout and the search all speak of that one.
  const shown = inGamut(value);
  const css = oklchToCSS(value);

  return (
    <Stack spacing={1} sx={{ width: AREA_WIDTH }}>
      <Box sx={{ position: "relative", lineHeight: 0 }}>
        <Box
          component="canvas"
          ref={areaRef}
          onPointerDown={drag(pickInArea)}
          onPointerMove={drag(pickInArea)}
          sx={{
            width: AREA_WIDTH,
            height: AREA_HEIGHT,
            borderRadius: 1,
            cursor: "crosshair",
            display: "block",
          }}
        />
        <Box
          sx={{
            ...MARKER_STYLE,
            width: DOT_SIZE,
            height: DOT_SIZE,
            margin: `${-DOT_SIZE / 2}px 0 0 ${-DOT_SIZE / 2}px`,
            borderRadius: "50%",
            background: css,
            left: inset(
              (value.c / MAX_CHROMA) * AREA_WIDTH,
              AREA_WIDTH,
              DOT_SIZE,
            ),
            top: inset((1 - value.l) * AREA_HEIGHT, AREA_HEIGHT, DOT_SIZE),
          }}
        />
      </Box>
      <Box sx={{ position: "relative", lineHeight: 0 }}>
        <Box
          component="canvas"
          ref={stripRef}
          onPointerDown={drag(pickHue)}
          onPointerMove={drag(pickHue)}
          sx={{
            width: AREA_WIDTH,
            height: STRIP_HEIGHT,
            borderRadius: 1,
            cursor: "ew-resize",
            display: "block",
          }}
        />
        <Box
          sx={{
            ...MARKER_STYLE,
            top: 0,
            width: BAR_WIDTH,
            height: STRIP_HEIGHT,
            marginLeft: `${-BAR_WIDTH / 2}px`,
            borderRadius: 1,
            background: css,
            left: inset((value.h / 360) * AREA_WIDTH, AREA_WIDTH, BAR_WIDTH),
          }}
        />
      </Box>
      <Stack direction="row" spacing={1} alignItems="center">
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1,
            background: css,
            boxShadow: "inset 0 0 0 1px rgba(128, 128, 128, 0.45)",
          }}
        />
        <Typography variant="caption" color="text.secondary">
          L {shown.l.toFixed(3)} · C {shown.c.toFixed(3)} · h {shown.h.toFixed(1)}°
        </Typography>
      </Stack>
    </Stack>
  );
};

export default OKLChPicker;
